import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DollarSign, EyeOff, ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { SwitchToggle } from './dialogs/SwitchToggle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Admin control: global master switch + per-brand overrides for showing fabric prices on the public site.
export const PublicPriceToggle = ({ token }) => {
  const [settings, setSettings] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/fabric-collections/public-settings`, { headers })
      .then(r => setSettings(r.data))
      .catch(() => setSettings({ show_prices: false, brands: [] }));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (body, msg) => {
    try {
      const { data } = await axios.put(`${API_URL}/api/admin/fabric-collections/public-settings`, body, { headers });
      setSettings(data);
      toast.success(msg);
    } catch {
      toast.error('Failed to update price visibility');
    }
  };

  if (!settings) return null;
  const visibleBrands = settings.brands.filter(b => b.effective).map(b => b.name);
  const label = !settings.show_prices ? 'Hidden' : visibleBrands.length === settings.brands.length ? 'Visible' : visibleBrands.length ? visibleBrands.join(', ') : 'Hidden (all brands off)';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="public-price-toggle"
          className={`inline-flex items-center gap-2 h-8 px-3 rounded-md border text-xs font-medium transition-colors ${visibleBrands.length ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-neutral-300 bg-white text-neutral-600 hover:bg-neutral-50'}`}
          title="Controls whether material/colour prices appear in the public Fabric Collection pages"
        >
          {visibleBrands.length ? <DollarSign className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          Prices online: <span data-testid="public-price-label">{label}</span>
          <ChevronDown className="w-3 h-3 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-4 space-y-4" data-testid="public-price-popover">
        <SwitchToggle
          label="Show prices online" hint="Master switch for the public website"
          checked={settings.show_prices}
          onToggle={() => save({ show_prices: !settings.show_prices }, !settings.show_prices ? 'Fabric prices are now visible on the website' : 'Fabric prices hidden from the website')}
          testId="public-price-master"
        />
        <div className={`space-y-3 border-t pt-3 ${settings.show_prices ? '' : 'opacity-50 pointer-events-none'}`}>
          <p className="text-[11px] uppercase tracking-wider text-neutral-500">Per brand</p>
          {settings.brands.map(b => (
            <SwitchToggle
              key={b.id} label={b.name} hint={b.effective ? 'Prices visible' : 'Prices hidden'}
              checked={b.show_prices}
              onToggle={() => save({ brand_prices: { [b.id]: !b.show_prices } }, `${b.name} prices ${!b.show_prices ? 'visible' : 'hidden'} online`)}
              testId={`public-price-brand-${b.name}`}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default PublicPriceToggle;
